import { chartTypeSortingMode } from "../chart-type";
import { referencedConceptUuids } from "./reducer";
import { type WorkspaceState, privateConcepts, resolveConcepts } from "./state";

/**
 * What a workspace is, stated once instead of being re-derived by every reader that guards against it.
 *
 * These are **enforced**, not measured: a document is validated and repaired once, at ingestion
 * (`ingest.ts`), so everything downstream may assume it resolves — and after that any violation is a
 * programming error, which throws. That is a deliberate change of position — D0008. Six of the seven were broken by
 * live operations before this commit, each in a way a downstream `if (!concept) return null` was quietly
 * absorbing; the operations were changed instead.
 *
 * The rule for adding one: an invariant is a property of the **document** that some reader would otherwise
 * have to guard against. A property of a *form submission* is not one — it belongs in that form's schema,
 * where a user can be told about it, rather than here, where the only vocabulary is a thrown error.
 */
export type Invariant = {
	name: InvariantName;
	check(state: WorkspaceState): string[];
};

export type InvariantName =
	| "poolOrderedByOrigin"
	| "privateConceptUuidsUnique"
	| "conceptReferencesResolve"
	| "noOrphanPrivateConcepts"
	| "chartHasSeries"
	| "chartSortOwnership"
	| "stackedColumnHasBreakdown";

export type InvariantViolation = { name: InvariantName; reason: string };

export const invariants: Invariant[] = [
	{
		/**
		 * The pool's ordering rule, and the reason it can be one list instead of two: private first, shared
		 * last. Break it and `parameterizedConcepts` or `grid.projection` silently reorders, which reorders
		 * the user's columns.
		 */
		name: "poolOrderedByOrigin",
		check(state) {
			const firstShared = state.concepts.findIndex((entry) => entry.origin === "STANDARD");
			if (firstShared === -1) {
				return [];
			}
			return state.concepts
				.slice(firstShared)
				.filter((entry) => entry.origin === "WORKSPACE")
				.map((entry) => `private concept ${entry.concept.uuid} sits after a shared one`);
		},
	},
	{
		/**
		 * Two private entries for one uuid would make `patchPrivateConcept` update one and the serializer
		 * persist both.
		 */
		name: "privateConceptUuidsUnique",
		check(state) {
			const seen = new Set<string>();
			const duplicates = new Set<string>();
			for (const concept of privateConcepts(state.concepts)) {
				if (seen.has(concept.uuid)) {
					duplicates.add(concept.uuid);
				}
				seen.add(concept.uuid);
			}
			return [...duplicates].map((uuid) => `private concept ${uuid} appears twice in the pool`);
		},
	},
	{
		/**
		 * Every uuid the document points at resolves in the pool — from all five places that can point at one:
		 * `columns`, `charts.series`, `charts.relativeThreshold`, `filters[].parameterizedConceptUuid` and
		 * `sort`.
		 *
		 * This is the one the rest of the area was paying for. `workspaceChart/index.tsx` computes `isBroken`
		 * from an unresolvable series concept, the table settings panel returns `null` for a column whose
		 * concept is missing, and the chart and filter panels each guard the same way — four readers answering
		 * "did it resolve" because nothing upstream promised it did.
		 */
		name: "conceptReferencesResolve",
		check(state) {
			const pool = new Set(resolveConcepts(state.concepts).map((concept) => concept.uuid));
			return [...referencedConceptUuids(state)]
				.filter((uuid) => !pool.has(uuid))
				.map((uuid) => `${uuid} is referenced but not in the pool`);
		},
	},
	{
		/**
		 * No unreferenced **private** concept survives. Shared ones are exempt: they have their own API and
		 * outlive the workspace, so an unreferenced one is not garbage, it is somebody else's column.
		 *
		 * An orphan is invisible until it is persisted, and then it grows: every one rides every subsequent PUT
		 * for the life of the workspace.
		 */
		name: "noOrphanPrivateConcepts",
		check(state) {
			const referenced = referencedConceptUuids(state);
			return privateConcepts(state.concepts)
				.filter((concept) => !referenced.has(concept.uuid))
				.map((concept) => `private concept ${concept.uuid} is referenced by nothing`);
		},
	},
	{
		/** A chart plots something. One with no series has no reason to exist and nothing to draw. */
		name: "chartHasSeries",
		check(state) {
			return state.content.charts.flatMap((chart, index) =>
				chart.series.length === 0 ? [`chart ${index} (${chart.label ?? chart.chartType}) has no series`] : [],
			);
		},
	},
	{
		/**
		 * Only a chart whose type *sorts* may own a sort direction.
		 *
		 * Narrower than the plan's "only a `WORKSPACE`-sorting chart may own `sort`", and deliberately: a
		 * `STACKED_COLUMN` legitimately persists a direction in the same preference — its own, client-side, over
		 * a breakdown category (`chartTypeSortingMode` → `CATEGORY`). What no chart type may own is a sort it has no
		 * way to apply, i.e. sorting mode `NONE`. Reachable by changing a sorted column chart into a line, which
		 * is why `editChart` now clamps it.
		 */
		name: "chartSortOwnership",
		check(state) {
			return state.content.charts.flatMap((chart, index) =>
				chart.preferences?.sort != null && chartTypeSortingMode(chart.chartType) === "NONE"
					? [`chart ${index} is a ${chart.chartType} and cannot sort, yet owns a sort direction`]
					: [],
			);
		},
	},
	{
		/**
		 * A stacked column has a breakdown, because it has nothing to stack without one.
		 *
		 * Enforceable here only because the form says it first: `chartSettingsResolverSchema` requires
		 * `breakdown.dimension` for the type ("Break a level down with “All” first"), and the chart-type picker
		 * cannot offer it without one. So this is the second line of defence rather than the only one, and it cannot
		 * turn a missing form field into a thrown error. Ingestion is what handles the case that comment
		 * anticipates — a persisted chart whose concept lost its breakdown.
		 */
		name: "stackedColumnHasBreakdown",
		check(state) {
			return state.content.charts.flatMap((chart, index) =>
				chart.chartType === "STACKED_COLUMN" && chart.breakdownProperties?.dimension == null
					? [`chart ${index} is a stacked column with no breakdown dimension`]
					: [],
			);
		},
	},
];

/** Every violation in the state. */
export function inspectWorkspace(state: WorkspaceState): InvariantViolation[] {
	return invariants.flatMap((invariant) => invariant.check(state).map((reason) => ({ name: invariant.name, reason })));
}

export class WorkspaceInvariantError extends Error {
	constructor(
		context: string,
		readonly violations: InvariantViolation[],
	) {
		super(
			`the workspace broke its own invariants ${context} — ${violations
				.map((violation) => `${violation.name}: ${violation.reason}`)
				.join("; ")}`,
		);
		this.name = "WorkspaceInvariantError";
	}
}

/**
 * Throws unless the state satisfies every invariant. **In production too**, and that is the point: a broken
 * reference reaches the user either as a chart that renders nothing or as a PUT that persists the breakage,
 * and both are worse than a stack trace naming the operation that caused it.
 *
 * The cost is bounded by *where* it is called from, not by a dev-mode flag. `reduce` runs it after content
 * actions only, which are human-paced — a click, a drag, a panel submit. The high-frequency writes are
 * `setSearch` (per keystroke) and `setSelection` (per row click), and an `EphemeralAction` provably cannot
 * violate anything: it reaches neither `concepts` nor `content`, which the property tests assert by identity
 * rather than by deep equality. Ingestion pays for it once per document.
 */
export function assertWorkspaceInvariants(state: WorkspaceState, context: string): void {
	const violations = inspectWorkspace(state);
	if (violations.length > 0) {
		throw new WorkspaceInvariantError(context, violations);
	}
}
